import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box, Typography, Button, Alert, CircularProgress,
} from '@mui/material';
import {
  HomeOutlined, ChevronRightOutlined,
  SettingsOutlined, AssignmentOutlined, TvOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { StatusChip } from '../../components/DashboardShared';
import { PRIMARY, TabPanel } from './shared';
import EventDetailsTab  from './EventDetailsTab';
import FormsTab         from './formBuilderTab/FormBuilderTab';
import HomePageTab      from './eventPagesTab/HomepageTab';
import LobbyBuilderTab  from './lobbyBuilderTab/LobbyBuilderTab';

// Placeholder
const PlaceholderTab = ({ label, icon }) => (
  <Box sx={{ pt: 3, textAlign: 'center', py: 10 }}>
    <Box sx={{ fontSize: 44, mb: 2, opacity: 0.2 }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Coming soon</Typography>
  </Box>
);

/**
 * ALL_TABS — master list in display order.
 * `permission`  — role permission key required for this tab to be visible.
 * `accessCheck` — optional extra fn(auth) → bool for resource-level checks
 *                 (e.g. lobby/form allow-list). Receives the full auth context.
 */
const ALL_TABS = [
  {
    label: 'Event Details',
    icon: <SettingsOutlined sx={{ fontSize: 16 }} />,
    slug: 'details',
    permission: 'events:view',
  },
  {
    label: 'Event Page',
    icon: <HomeOutlined sx={{ fontSize: 16 }} />,
    slug: 'pages',
    permission: 'events:edit',
  },
  {
    label: 'Forms',
    icon: <AssignmentOutlined sx={{ fontSize: 16 }} />,
    slug: 'forms',
    permission: 'forms:view',
    // Visible only when the user has at least one accessible form (or no restriction)
    accessCheck: (auth) => {
      const allowed = auth.user?.allowedForms;
      return !allowed || allowed.length === 0;
    },
  },
  {
    label: 'Virtual Event',
    icon: <TvOutlined sx={{ fontSize: 16 }} />,
    slug: 'virtual-event',
    permission: 'events:edit',
    // Visible only when the user has at least one accessible lobby (or no restriction)
    accessCheck: (auth) => {
      const allowed = auth.user?.allowedLobbies;
      return !allowed || allowed.length === 0;
    },
  },
];

export default function EventEditor() {
  const { id: eventId } = useParams();
  const { pathname } = useLocation();

  // Parse tab from the splat portion: /editor/:id/<tab>
  // Subtab lives in ?t=<subtab> and is owned entirely by EventDetailsTab
  const pathParts   = pathname.replace(`/editor/${eventId}/`, '').split('/').filter(Boolean);
  const tabSlug     = pathParts[0] ?? null;
  const navigate = useNavigate();
  const auth = useAuth();
  const { canAccessEvent, hasPermission, isSuperAdmin } = auth;

  const [event, setEvent]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  // Lazy-mount top-level tabs: only mount a tab the first time it's visited,
  // then keep it mounted (display:none when inactive) so state is preserved.
  const [activatedSlugs, setActivatedSlugs] = useState({});

  // ── Compute which tabs this user can see ────────────────────────────────────
  const visibleTabs = useMemo(() => {
    return ALL_TABS.filter(t => {
      // Super admins always see everything
      if (isSuperAdmin) return true;
      // Must have the required permission
      if (!hasPermission(t.permission)) return false;
      // Optional resource-level check (allowedLobbies / allowedForms)
      if (t.accessCheck && !t.accessCheck(auth)) return false;
      return true;
    });
  }, [isSuperAdmin, hasPermission, auth]);

  // Derive active tab from URL slug; fall back to first visible tab
  const activeTab = useMemo(() => {
    const fromUrl = visibleTabs.find(t => t.slug === tabSlug);
    return fromUrl || visibleTabs[0] || null;
  }, [visibleTabs, tabSlug]);

  const goToTab = useCallback((slug) => {
    navigate(`/editor/${eventId}/${slug}`);
  }, [eventId, navigate]);

  // Mark slug as activated when first visited
  useEffect(() => {
    if (activeTab?.slug) {
      setActivatedSlugs(prev => prev[activeTab.slug] ? prev : { ...prev, [activeTab.slug]: true });
    }
  }, [activeTab?.slug]); // eslint-disable-line

  // Merge partial event updates from sub-tabs without triggering a full refetch.
  // EventScheduleTab passes { schedule } or { speakers }; others pass nothing.
  const handleDetailsSave = useCallback((partial) => {
    if (partial && Object.keys(partial).length > 0) {
      setEvent(prev => prev ? { ...prev, ...partial } : prev);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get(`/events/${eventId}`);
      setEvent(r.data.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        navigate('/dashboard/events', { replace: true });
        return;
      }
      setError(e?.response?.data?.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  // Redirect away if no event access at all
  useEffect(() => {
    if (!canAccessEvent(eventId)) {
      navigate('/dashboard/events', { replace: true });
    }
  }, [eventId, canAccessEvent, navigate]);

  // If the URL slug is not in visibleTabs, redirect to the first visible tab
  useEffect(() => {
    if (visibleTabs.length === 0) return; // no tabs — nothing to redirect to
    const slugInVisible = visibleTabs.find(t => t.slug === tabSlug);
    if (!slugInVisible && visibleTabs[0]) {
      navigate(`/editor/${eventId}/${visibleTabs[0].slug}`, { replace: true });
    }
  }, [tabSlug, visibleTabs, eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <Box sx={{
      minHeight: '100vh',
      background: 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <CircularProgress sx={{ color: PRIMARY }} />
    </Box>
  );

  if (error) return <Alert severity="error" sx={{ m: 3, borderRadius: 2 }}>{error}</Alert>;
  if (!event) return null;

  // No visible tabs at all → show a friendly denied screen
  if (visibleTabs.length === 0) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
      <Typography sx={{ fontWeight: 700, color: 'text.secondary' }}>You don't have access to any tabs for this event.</Typography>
      <Button onClick={() => navigate('/dashboard/events')}>Back to Events</Button>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* ── Sticky header ──────────────────────────────────────────────────────── */}
      <Box sx={{
        background: '#fff',
        borderBottom: '1px solid #e8edf2',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        position: 'sticky', top: 0, zIndex: 100,
        px: { xs: 2, md: 3 },
        height: 60,
        display: 'grid', alignItems: 'center',
        gap: 2,
        gridTemplateColumns: '1fr 1fr 1fr',
      }}>

        {/* Left: breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, minWidth: 0 }}>
          <Button
            onClick={() => navigate('/dashboard/events')}
            startIcon={<HomeOutlined sx={{ fontSize: 15 }} />}
            sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.875rem', px: 0.5, minWidth: 0, flexShrink: 0, '&:hover': { background: 'transparent', color: PRIMARY } }}
          >
            Events
          </Button>
          <ChevronRightOutlined sx={{ fontSize: 15, color: '#c0c8d4', flexShrink: 0 }} />
          <Typography sx={{
            fontSize: '0.875rem', fontWeight: 700, color: '#1a1a2e',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            maxWidth: { xs: 100, sm: 180, md: 260 },
          }}>
            {event.name}
          </Typography>
          <Box sx={{ flexShrink: 0 }}>
            <StatusChip status={event.status} />
          </Box>
        </Box>

        {/* Center: pill tabs — only visible tabs rendered */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#f0f4f8', border: '1px solid #e4e9f0',
          borderRadius: 3, p: '3px', gap: '2px', flexShrink: 0,
        }}>
          {visibleTabs.map((t) => {
            const active = activeTab?.slug === t.slug;
            return (
              <Box
                key={t.slug}
                onClick={() => goToTab(t.slug)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 1.75, height: 34, borderRadius: 2.25, cursor: 'pointer',
                  background: active ? '#fff' : 'transparent',
                  color: active ? PRIMARY : '#5a6474',
                  fontWeight: active ? 700 : 500,
                  fontSize: '0.85rem',
                  boxShadow: active ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  border: active ? '1px solid #e4e9f0' : '1px solid transparent',
                  transition: 'all 0.15s ease',
                  userSelect: 'none', whiteSpace: 'nowrap',
                  '&:hover': { color: active ? PRIMARY : '#1a1a2e' },
                }}
              >
                <Box sx={{ display: 'flex', color: 'inherit', '& svg': { fontSize: 15 } }}>{t.icon}</Box>
                <Box component="span">{t.label}</Box>
              </Box>
            );
          })}
        </Box>

        {/* Right: reserved for Save button */}
        <Box sx={{ flexShrink: 0 }} />
      </Box>

      {/* ── Page body ──────────────────────────────────────────────────────── */}
      <Box sx={{
        flex: 1,
        background: 'white',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative blobs */}
        {/* <Box sx={{ position: 'absolute', width: 500, height: 500, top: -150, right: -100, background: `radial-gradient(circle, ${alpha(PRIMARY, 0.07)} 0%, transparent 70%)`, borderRadius: '50%', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 400, height: 400, bottom: -100, left: -80, background: `radial-gradient(circle, ${alpha('#00a882', 0.06)} 0%, transparent 70%)`, borderRadius: '50%', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(${alpha(PRIMARY, 0.08)} 1px, transparent 1px)`, backgroundSize: '28px 28px', pointerEvents: 'none' }} /> */}

        {/* Tab content — lazy-mounted: only renders once first visited, then stays
            mounted with display:none when inactive to preserve state cheaply. */}
        {activatedSlugs['details'] && (
          <Box sx={{ display: activeTab?.slug === 'details' ? 'block' : 'none', position: 'relative', zIndex: 1 }}>
            <EventDetailsTab event={event} onSave={handleDetailsSave} />
          </Box>
        )}
        {activatedSlugs['pages'] && (
          <Box sx={{ display: activeTab?.slug === 'pages' ? 'block' : 'none', position: 'relative', zIndex: 1 }}>
            <HomePageTab event={event} />
          </Box>
        )}
        {activatedSlugs['forms'] && (
          <Box sx={{ display: activeTab?.slug === 'forms' ? 'block' : 'none', position: 'relative', zIndex: 1 }}>
            <FormsTab event={event} />
          </Box>
        )}
        {activatedSlugs['virtual-event'] && (
          <Box sx={{ display: activeTab?.slug === 'virtual-event' ? 'block' : 'none', position: 'relative', zIndex: 1 }}>
            <LobbyBuilderTab event={event} />
          </Box>
        )}
      </Box>
    </Box>
  );
}